if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      let addSucceeded = false;

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.setSubmitLoadingState(true);

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartData = await ProductForm.fetchCart();
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);
        addSucceeded = true;

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // Update cart drawer contents immediately after Shopify returns the add response.
          if (this.cartDrawer) {
            this.cartDrawer.renderContents(
              data,
              !quickAddDrawer && theme.settings.afterAtc === 'drawer'
            );
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refresh();
              }
            }
          }
        }

        this.dispatchCartEvents(data, oldCartData);
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        if (addSucceeded) this.setSubmitSuccessState(true);
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.setSubmitLoadingState(false);
          this.setSubmitSuccessState(false);
        }, addSucceeded ? 900 : 0);
      }
    }

    /**
     * Fetches the current cart, swallowing failures so UI updates are not blocked.
     * @returns {Promise<object|null>}
     */
    static async fetchCart() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) return null;
        return response.json();
      } catch (error) {
        return null;
      }
    }

    /**
     * Fires cart events after the visible UI has already been updated.
     * @param {object} data - Add-to-cart response.
     * @param {object|null} oldCartData - Cart before the add request.
     */
    async dispatchCartEvents(data, oldCartData) {
      const newCartData = await ProductForm.fetchCart();
      const cart = newCartData || data;
      const itemInOldCart = oldCartData?.items?.filter(
        (item) => item.variant_id === data.variant_id
      )[0];

      // Check if product was already in the cart.
      if (itemInOldCart) {
        this.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true,
          detail: {
            cart,
            variantId: data.variant_id,
            oldQuantity: itemInOldCart.quantity,
            newQuantity: (itemInOldCart.quantity === data.quantity)
              ? itemInOldCart.quantity : data.quantity
          }
        }));
      } else {
        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: {
            cart,
            variantId: data.variant_id
          }
        }));
      }
    }

    /**
     * Sets visible loading feedback on the main PDP button and sticky PDP button.
     * @param {boolean} isLoading - Loading state.
     */
    setSubmitLoadingState(isLoading) {
      const buttons = this.getFeedbackButtons();

      buttons.forEach((button) => {
        if (!button) return;

        if (isLoading) {
          button.dataset.loadingOriginalText = button.dataset.loadingOriginalText || button.textContent.trim();
          button.classList.add('is-loading');
          button.setAttribute('aria-busy', 'true');
          if (this.closest('.cc-main-product')) button.textContent = 'Adding...';
        } else {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
          if (button.dataset.loadingOriginalText) {
            button.textContent = button.dataset.loadingOriginalText;
            delete button.dataset.loadingOriginalText;
          }
        }
      });
    }

    /**
     * Sets success feedback on the main PDP button and sticky PDP button.
     * @param {boolean} isSuccess - Success state.
     */
    setSubmitSuccessState(isSuccess) {
      this.getFeedbackButtons().forEach((button) => {
        if (!button) return;
        button.classList.toggle('is-success', isSuccess);
        if (isSuccess && this.closest('.cc-main-product')) button.textContent = 'Added';
      });
    }

    /**
     * Returns buttons that should mirror Add-to-Cart feedback.
     * @returns {Array}
     */
    getFeedbackButtons() {
      const productSection = this.closest('.cc-main-product');
      const stickySubmit = productSection?.querySelector('.js-sticky-atc-submit');
      return [this.submitBtn, stickySubmit].filter(Boolean);
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
